import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { discoveryKeys } from './discovery';
import { QuotedEvidenceSchema } from './discovery-findings';
import {
  engagementRequirementKeys,
  newIdempotencyKey,
  type Criticality,
} from './engagement-requirements';

/**
 * Selection -> Requirements -> Add from discovery (T-0190, E12-F10, over
 * the T-0179 API): requirement suggestions built by AI from confirmed
 * evidence only, reviewed and added by a consultant.
 *
 * Under `/api/v1/engagements/:engagement_id/requirements/discovery-suggestions`.
 * `not_found` when the Selection or Discovery module is off. Support
 * counts, confidence, the pre-selection, matches and the number a
 * suggestion lands as are all computed by the server; the screen shows
 * them. `:add` is idempotent and links evidence to a matching requirement
 * instead of creating a second one.
 */

const ModuleRef = z.object({ id: z.string(), name: z.string() });

export const SuggestionSchema = z.object({
  id: z.string(),
  status: z.enum(['open', 'added', 'linked', 'dismissed', 'withdrawn']),
  library_item: z.object({ id: z.string(), number: z.string(), module: ModuleRef }).nullable(),
  /** Up to two other library items it could be instead (Decision 54.35). */
  alternatives: z.array(z.object({ id: z.string(), number: z.string(), text: z.string() })),
  module: z.object({ id: z.string(), name: z.string(), prefix: z.string() }),
  text: z.string(),
  evidence: z.array(QuotedEvidenceSchema),
  /** People (merges resolved) and documents behind its own evidence (Decision 54.36). */
  support: z.object({ people: z.number(), documents: z.number() }),
  /** Calibrated by code; describes the suggestion, never counted. */
  confidence: z.number(),
  preselected: z.boolean(),
  picked: z.boolean(),
  suggested_criticality: z.string().nullable(),
  criticality: z.string(),
  /** An engagement requirement it matches: adding links its evidence there. */
  match: z
    .object({ requirement_id: z.string(), number: z.string(), status: z.string() })
    .nullable(),
  near: z.array(z.object({ number: z.string(), note: z.string() })),
  lands_in: z.object({ module: ModuleRef, number_preview: z.string() }),
  dismiss_reason: z.string().nullable(),
  dismiss_text: z.string().nullable(),
  result_requirement: z.object({ id: z.string(), number: z.string() }).nullable(),
  decided_at: z.string().nullable(),
});
export type Suggestion = z.infer<typeof SuggestionSchema>;

const SuggestionsSchema = z.object({
  last_run: z
    .object({
      at: z.string(),
      by: z.object({ user_id: z.string(), name: z.string() }).nullable(),
      evidence_read: z.number(),
      cost_cents: z.number(),
    })
    .nullable(),
  /** A run being read now: the screen asks again until it lands. */
  active_run: z.object({ run_id: z.string(), status: z.string() }).nullable(),
  /** Confirmed since the last run: "Run again" reads only these. */
  evidence_since_last_run: z.number(),
  /** Evidence still awaiting review, which no run reads (rule 12). */
  awaiting_review_not_used: z.number(),
  groups: z.object({
    library: z.array(SuggestionSchema),
    new: z.array(SuggestionSchema),
    added: z.array(SuggestionSchema),
    linked: z.array(SuggestionSchema),
    dismissed: z.array(SuggestionSchema),
    withdrawn: z.array(SuggestionSchema),
  }),
});
export type Suggestions = z.infer<typeof SuggestionsSchema>;

export const SUGGESTION_DISMISS_REASONS = [
  'out_of_scope',
  'already_covered',
  'not_what_they_meant',
  'other',
] as const;
export type SuggestionDismissReason = (typeof SUGGESTION_DISMISS_REASONS)[number];

const AddResultSchema = z.object({
  added: z.array(z.object({ id: z.string(), requirement_id: z.string(), number: z.string() })),
  linked: z.array(
    z.object({
      id: z.string(),
      requirement_id: z.string(),
      number: z.string(),
      restored: z.boolean(),
    }),
  ),
  skipped: z.array(z.object({ id: z.string(), reason: z.string() })),
});
export type AddSuggestionsResult = z.infer<typeof AddResultSchema>;

export interface SuggestionPatch {
  picked?: boolean;
  criticality?: Criticality;
  library_item_id?: string;
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

function base(engagementId: string): string {
  return `/api/v1/engagements/${encodeURIComponent(engagementId)}/requirements/discovery-suggestions`;
}

export const getSuggestions = (engagementId: string) =>
  apiClient(base(engagementId), SuggestionsSchema);

export const runSuggestions = (engagementId: string) =>
  apiClient(`${base(engagementId)}:run`, z.object({ run_id: z.string() }), {
    method: 'POST',
    body: {},
    idempotencyKey: newIdempotencyKey(),
  });

export const patchSuggestion = (engagementId: string, id: string, body: SuggestionPatch) =>
  apiClient(`${base(engagementId)}/${encodeURIComponent(id)}`, z.unknown(), {
    method: 'PATCH',
    body,
    idempotencyKey: newIdempotencyKey(),
  });

export const addSuggestions = (engagementId: string, ids: string[], key: string) =>
  apiClient(`${base(engagementId)}:add`, AddResultSchema, {
    method: 'POST',
    body: { ids },
    idempotencyKey: key,
  });

export const dismissSuggestion = (
  engagementId: string,
  id: string,
  reason: SuggestionDismissReason,
  text?: string,
) =>
  apiClient(`${base(engagementId)}/${encodeURIComponent(id)}:dismiss`, z.unknown(), {
    method: 'POST',
    body: { reason, ...(text ? { text } : {}) },
    idempotencyKey: newIdempotencyKey(),
  });

export const restoreSuggestion = (engagementId: string, id: string) =>
  apiClient(`${base(engagementId)}/${encodeURIComponent(id)}:restore`, z.unknown(), {
    method: 'POST',
    body: {},
    idempotencyKey: newIdempotencyKey(),
  });

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const suggestionKeys = {
  list: (engagementId: string) => ['requirement-suggestions', engagementId] as const,
};

/** `enabled` false skips the read (the Overview card, when Discovery is off). */
export function useSuggestionsQuery(engagementId: string, enabled = true) {
  return useQuery({
    queryKey: suggestionKeys.list(engagementId),
    queryFn: () => getSuggestions(engagementId),
    enabled,
    // While a run is being read, look again every few seconds until it lands.
    refetchInterval: (q) => (q.state.data?.active_run ? 4000 : false),
    staleTime: 10_000,
    retry: false,
  });
}

/** Writes refresh the suggestions, the requirements list and Discovery (sign-off can reopen). */
function useSuggestionWrite<TVars, TResult>(
  engagementId: string,
  fn: (vars: TVars) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: suggestionKeys.list(engagementId) });
      void qc.invalidateQueries({ queryKey: engagementRequirementKeys.all(engagementId) });
      void qc.invalidateQueries({ queryKey: discoveryKeys.all(engagementId) });
    },
  });
}

export const useRunSuggestions = (engagementId: string) =>
  useSuggestionWrite(engagementId, () => runSuggestions(engagementId));

export const useAddSuggestions = (engagementId: string) =>
  useSuggestionWrite(engagementId, (v: { ids: string[]; key: string }) =>
    addSuggestions(engagementId, v.ids, v.key),
  );

export const useDismissSuggestion = (engagementId: string) =>
  useSuggestionWrite(
    engagementId,
    (v: { id: string; reason: SuggestionDismissReason; text?: string }) =>
      dismissSuggestion(engagementId, v.id, v.reason, v.text),
  );

export const useRestoreSuggestion = (engagementId: string) =>
  useSuggestionWrite(engagementId, (id: string) => restoreSuggestion(engagementId, id));

/**
 * Pick, criticality and switching a match are saved at once and shown
 * at once: the list is updated in place, then refreshed from the server.
 */
export function usePatchSuggestion(engagementId: string) {
  const qc = useQueryClient();
  const key = suggestionKeys.list(engagementId);
  return useMutation({
    mutationFn: (v: { id: string; patch: SuggestionPatch }) =>
      patchSuggestion(engagementId, v.id, v.patch),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<Suggestions>(key);
      if (before && (v.patch.picked !== undefined || v.patch.criticality !== undefined)) {
        const apply = (s: Suggestion): Suggestion =>
          s.id === v.id
            ? {
                ...s,
                ...(v.patch.picked !== undefined ? { picked: v.patch.picked } : {}),
                ...(v.patch.criticality !== undefined ? { criticality: v.patch.criticality } : {}),
              }
            : s;
        qc.setQueryData<Suggestions>(key, {
          ...before,
          groups: {
            ...before.groups,
            library: before.groups.library.map(apply),
            new: before.groups.new.map(apply),
          },
        });
      }
      return { before };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.before) qc.setQueryData(key, ctx.before);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  });
}
